import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import {
  Badge,
  Button,
  Grid2,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { HomeLinkRoutes } from 'constants/routes';
import {
  AppointmentType,
  useAppointment,
  useRequestingAppointments,
} from 'hooks/useAppointments';
import LogoutIcon from '@mui/icons-material/Logout';
import { IAppointment } from 'model/appointment';
import { MobileHeader } from './MobileHeader';
import { useNotification } from 'utils/NotificationProvider';
import { NotificationList } from 'views/Patient/notifications';
import { useAuth } from 'utils/AuthProvider';
import strings from 'constants/strings';
import { handleErrorToast } from 'components/Toast';
import { PatientSwitch } from './PatientSwitch/PatientSwitch';
import { ScreenView, ViewMode } from 'views/Medical/types';
import { Logout } from 'views/PreLogin/Logout';
import MenuIcon from 'Icons/calendar/menu_green.svg';
import MenuIconActive from 'Icons/calendar/menu_white.svg';
import NotificationIcon from 'Icons/calendar/notification.svg';
import ChatIcon from 'Icons/chat/chatIcon.svg';
import SettingIcon from 'Icons/calendar/setting.svg';
import { useMedicalView } from 'utils/MedicalViewProvider';
import { DateSelector } from 'views/Medical/DateSelector';
import {
  getAvailabilityText,
  getDateAvailability,
} from '~/views/Medical/HoursView/helpers';
import dayjs from 'dayjs';
import accountStore from '~/store/accountStore';
import medicalCareStore from '~/store/medicalCareStore';
import { DaySettingDialog } from '~/views/Medical/HoursSetting/DaySettingDialog';
import { HoursSettingDialog } from '~/views/Medical/HoursSetting/HoursSettingDialog';
import { useChat } from '~/utils/ChatProvider';
import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';

const styles = {
  inputContainer: {
    height: 40,
  },
  tileContainer: {
    maxWidth: 510,
    paddingLeft: 8,
    paddingRight: 8,
  },
  leftMenuContainer: {
    left: 0,
  },
  actionContainer: {
    right: 0,
  },
  icon: {
    width: 20,
    height: 20,
    padding: 6,
  },
  chatIcon: {
    width: 22,
    height: 22,
    padding: 4,
  },
  calendar: {
    width: 32,
    height: 32,
  },
  menuIconContainerStyle: {
    width: 96,
    height: 72,
  },
  hoursButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    color: '#00c0b4',
    borderColor: '#00c0b4',
    borderRadius: 18,
    height: 36,
  },
};

export const getDoctorHeaderName = (
  pathname: string,
  appointment?: IAppointment,
) => {
  if (!appointment) {
    return '';
  }
  if (appointment.appointmentType === AppointmentType.medicalcare) {
    return `診療中（ ${appointment.patientName || appointment.patientEmail} ）`;
  }
  if (appointment.appointmentType === AppointmentType.medicine_delivery) {
    return `お薬サービス中（ ${appointment.patientName || appointment.patientEmail} ）`;
  }
  return '';
};

export const MedicalHeader = ({ type }: { type: AppointmentType }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { medicalHeaderHeight, isMobile, mainScreenUsableSize } =
    useWindowDimensions();
  const isDoctor = accountStore.isDoctor;
  const { requestingAppointments = [] } = useRequestingAppointments(
    isDoctor ? AppointmentType.medicalcare : AppointmentType.medicine_delivery,
  );
  const { selectedDate } = useMedicalView();
  const { doctor, pharmacy } = medicalCareStore;
  const businessHours = useMemo(() => {
    return accountStore.isDoctor
      ? doctor?.businessHours
      : pharmacy?.businessHours;
  }, [accountStore.isDoctor, doctor?.businessHours, pharmacy?.businessHours]);
  const { totalUnreadCount } = useChat();
  const { appointmentId } = useParams();
  const { appointment } = useAppointment(appointmentId);
  const {
    isLeftMenuOpen,
    setIsLeftMenuOpen,
    dialogViewState,
    setDialogViewState,
    editDayDialogOpen,
    setEditDayDialogOpen,
  } = useMedicalView();
  const { dialogView } = dialogViewState;

  const isVideoCall = useMemo(() => {
    return pathname.includes(
      HomeLinkRoutes.medicalVideoCall.replace(':appointmentId', ''),
    );
  }, [pathname]);

  const isAppointmentView = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalAppointments);
  }, [pathname]);

  const isScheduleView = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalSchedule);
  }, [pathname]);

  const isAppointmentRequests = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalAppointmentRequests);
  }, [pathname]);

  const isPaymentHistory = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalPaymentHistory);
  }, [pathname]);

  const isChat = useMemo(() => {
    return pathname.includes(HomeLinkRoutes.medicalChat);
  }, [pathname]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const settingMenuOpen = Boolean(anchorEl);
  const userName = user?.name;
  const email = user?.email;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggle = () => {
    setIsLeftMenuOpen((prev) => !prev);
  };

  const openMessagePage = () => {
    setIsLeftMenuOpen(false);
    navigate(HomeLinkRoutes.medicalChat);
  };

  const openNotificationPage = () => {
    navigate(HomeLinkRoutes.medicalAppointmentRequests);
  };

  const viewHourSetting = () => {
    setDialogViewState({ dialogView: 'viewHourSettingDialog' });
  };

  const handleLogout = () => {
    navigate(HomeLinkRoutes.logout);
  };
  const currentBusinessHours: string[] = useMemo(() => {
    if (selectedDate) {
      const [currentYear, currentMonth, i] = selectedDate.split('-');
      const dayOfWeek = dayjs(selectedDate, 'YYYY-MM-DD').day();
      const { hours: availabilityList } = getDateAvailability(
        currentYear,
        currentMonth,
        parseInt(i),
        dayOfWeek,
        businessHours,
      );
      if (availabilityList.length === 0) {
        return [strings.hours.dayOff];
      }
      return availabilityList.map((a) => getAvailabilityText(a));
    } else {
      return ['-'];
    }
  }, [businessHours, selectedDate]);

  const handleCloseChat = () => {
    navigate(-1);
  };

  useEffect(() => {
    if ((isChat || isVideoCall) && isLeftMenuOpen) {
      setIsLeftMenuOpen(false);
    }
  }, [isChat, isVideoCall, isLeftMenuOpen, setIsLeftMenuOpen]);

  if (isVideoCall) {
    return (
      <Grid2
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        height={medicalHeaderHeight}
        sx={{
          minHeight: `${medicalHeaderHeight}px`,
          height: `${medicalHeaderHeight}px`,
        }}
      >
        <Typography className={'color--c-green font--28 fontWeight--600'}>
          {getDoctorHeaderName(pathname, appointment)}
        </Typography>
      </Grid2>
    );
  }

  if (isChat) {
    return (
      <Grid2
        display={'flex'}
        justifyContent="center"
        alignItems="center"
        height={medicalHeaderHeight}
        sx={{
          minHeight: `${medicalHeaderHeight}px`,
          height: `${medicalHeaderHeight}px`,
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingLeft: '40px',
          paddingRight: '30px',
        }}
      >
        <IconButton onClick={handleCloseChat} sx={{ p: '12px' }}>
          <CloseCircleOutlined style={{ fontSize: 32 }} />
        </IconButton>
      </Grid2>
    );
  }

  return (
    <Grid2
      display={'flex'}
      justifyContent="center"
      alignItems="center"
      height={medicalHeaderHeight}
      sx={{
        minHeight: `${medicalHeaderHeight}px`,
        height: `${medicalHeaderHeight}px`,
        paddingLeft: '40px',
        paddingRight: '30px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Grid2
        className={'flex flexRow flexAlignCenter posAbsolute'}
        style={styles.leftMenuContainer}
      >
        <Grid2
          className={`menuIconContainer flexCenterCenter ${
            isLeftMenuOpen ? 'bgColor--c-menu' : 'bgColor--white'
          }`}
          style={styles.menuIconContainerStyle}
        >
          <Grid2 className={'m16h cursorPointer'} onClick={toggle}>
            {isLeftMenuOpen ? (
              <img style={styles.icon} src={MenuIconActive} />
            ) : (
              <img style={styles.icon} src={MenuIcon} />
            )}
          </Grid2>
        </Grid2>
        <Typography className={'pl16 color--black font--16 fontWeight--800'}>
          {userName || email || '-'}
        </Typography>

        {isScheduleView && (
          <Button
            className="ml16 color--green border--green btn--primaryOutline"
            style={styles.hoursButton}
            onClick={viewHourSetting}
          >
            {strings.hours.hoursSetting}
          </Button>
        )}
        {isAppointmentView && !isAppointmentRequests && (
          <Button
            className="ml16 flexRow flexAlignCenter"
            sx={{ minHeight: 42 }}
            onClick={() => setEditDayDialogOpen(true)}
          >
            <Typography className={' color--green font--14 mr4'}>
              {`${strings.hours.hours}`}
            </Typography>
            <Grid2
              className="flexRow flexAlignCenter"
              style={{
                maxWidth: 285,
                flexWrap: 'wrap',
              }}
            >
              {currentBusinessHours.map((hour, index) => (
                <Typography key={index} className={'pl4 color--green font--14'}>
                  {index < currentBusinessHours.length - 1 ? `${hour}, ` : hour}
                </Typography>
              ))}
            </Grid2>
          </Button>
        )}
      </Grid2>
      {!isAppointmentRequests && !isPaymentHistory && <DateSelector />}
      <Grid2
        className={'flex flexRow flexAlignCenter pr16 posAbsolute'}
        style={styles.actionContainer}
      >
        <IconButton onClick={openMessagePage}>
          <Badge
            color="error"
            badgeContent={totalUnreadCount}
            sx={{
              '.MuiBadge-badge': {
                fontSize: 14,
                lineHeight: 20,
                padding: '0 6px',
              },
            }}
          >
            <img style={styles.chatIcon} src={ChatIcon} />
          </Badge>
        </IconButton>
        <IconButton onClick={openNotificationPage}>
          <Badge
            color="error"
            badgeContent={requestingAppointments.length}
            sx={{
              '.MuiBadge-badge': {
                fontSize: 14,
                lineHeight: 20,
                padding: '0 6px',
              },
            }}
          >
            <img style={styles.icon} src={NotificationIcon} />
          </Badge>
        </IconButton>
        {/*<IconButton>*/}
        {/*  <img style={styles.calendar} src={CalendarIcon} />*/}
        {/*</IconButton>*/}
        <IconButton onClick={handleClick}>
          <img style={styles.icon} src={SettingIcon} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={settingMenuOpen}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {strings.setting.action.logout}
          </MenuItem>
        </Menu>
      </Grid2>
      {editDayDialogOpen && (
        <DaySettingDialog
          replace={false}
          onClose={() => setEditDayDialogOpen(false)}
          defaultDateAvailabilityDate={selectedDate}
        />
      )}
      {dialogView === 'viewHourSettingDialog' && (
        <HoursSettingDialog
          onClose={() =>
            setDialogViewState((prev) => ({
              ...prev,
              dialogView: null,
            }))
          }
        />
      )}
    </Grid2>
  );
};
